export const LEGAL_UPDATED_AT = '26 de maio de 2026';

export type LegalSection = {
  title: string;
  paragraphs?: string[];
  items?: string[];
  subsections?: LegalSection[];
};

export const privacyIntro = {
  title: 'Política de Privacidade',
  subtitle: 'Proteção dos dados pessoais dos usuários da Constela Brasil',
  paragraphs: [
    'A Constela Brasil respeita a privacidade e proteção dos dados pessoais de seus usuários.',
    'Esta Política explica como coletamos, utilizamos, armazenamos e protegemos informações.',
  ],
};

export const privacySections: LegalSection[] = [
  {
    title: '1. Dados coletados',
    paragraphs: ['Poderemos coletar:'],
    items: [
      'Nome',
      'E-mail',
      'Telefone',
      'CPF',
      'Dados de pagamento',
      'Endereço IP',
      'Informações de navegação',
      'Dados de acesso',
      'Cookies',
      'Dados fornecidos em formulários',
    ],
  },
  {
    title: '2. Finalidade dos dados',
    paragraphs: ['Os dados poderão ser utilizados para:'],
    items: [
      'Cadastro de usuários',
      'Prestação de serviços',
      'Processamento de pagamentos',
      'Comunicação e suporte',
      'Melhorias da plataforma',
      'Segurança e prevenção a fraudes',
      'Campanhas de marketing',
      'Cumprimento de obrigações legais',
    ],
  },
  {
    title: '3. Compartilhamento de dados',
    paragraphs: ['Os dados poderão ser compartilhados com:'],
    items: [
      'Processadores de pagamento',
      'Ferramentas de análise',
      'Sistemas de hospedagem',
      'Parceiros tecnológicos',
      'Autoridades legais quando exigido',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['A Constela Brasil não comercializa dados pessoais.'],
      },
    ],
  },
  {
    title: '4. Segurança das informações',
    paragraphs: [
      'Adotamos medidas técnicas e organizacionais para proteção dos dados.',
      'Apesar disso, nenhum sistema é totalmente invulnerável.',
    ],
  },
  {
    title: '5. Direitos dos titulares',
    paragraphs: ['O usuário poderá solicitar:'],
    items: ['Correção de informações', 'Exclusão de dados'],
    subsections: [
      {
        title: '',
        paragraphs: ['Solicitações poderão ser feitas através dos canais oficiais da plataforma.'],
      },
    ],
  },
  {
    title: '6. Retenção dos dados',
    paragraphs: ['Os dados poderão ser armazenados pelo período necessário para:'],
    items: [
      'Cumprimento legal',
      'Exercício de direitos',
      'Prestação de serviços',
      'Segurança jurídica',
    ],
  },
  {
    title: 'Política de cookies',
    paragraphs: ['A plataforma utiliza cookies e tecnologias semelhantes para:'],
    items: [
      'Melhorar experiência do usuário',
      'Analisar tráfego',
      'Personalizar conteúdos',
      'Medir campanhas de marketing',
      'Realizar remarketing',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['Podemos utilizar ferramentas como:'],
        items: [
          'Meta Pixel',
          'Google Analytics',
          'Ferramentas de rastreamento',
          'Cookies funcionais',
          'Cookies de desempenho',
        ],
      },
      {
        title: '',
        paragraphs: ['O usuário poderá desativar cookies nas configurações do navegador.'],
      },
    ],
  },
  {
    title: 'Termo de consentimento terapêutico',
    paragraphs: [
      'Ao participar de atividades, sessões, constelações ou experiências disponibilizadas pela plataforma, o usuário declara estar ciente de que:',
    ],
    items: [
      'As práticas possuem caráter vivencial e de desenvolvimento pessoal',
      'Não substituem tratamento médico ou psicológico',
      'Não representam promessa de cura',
      'Podem despertar conteúdos emocionais sensíveis',
      'Cada participante é responsável por suas próprias decisões e interpretações',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['O participante declara participar de forma voluntária e consciente.'],
      },
    ],
  },
  {
    title: 'Integrações com Google (terapeutas)',
    paragraphs: [
      'Para login com Google e sincronização de agenda (terapeutas), acessamos e-mail, nome e eventos do Google Calendar conforme autorização do usuário, exclusivamente para autenticação e agendamento.',
      'Não vendemos nem usamos esses dados para publicidade. O usuário pode revogar permissões em https://myaccount.google.com/permissions.',
      'O uso está em conformidade com a Política de Dados do Usuário dos Serviços da API do Google.',
    ],
  },
];

export const termsIntro = {
  title: 'Termos de Uso',
  subtitle: 'Termos legais da plataforma Constela Brasil',
  paragraphs: [
    'Bem-vindo à plataforma Constela Brasil.',
    'Estes Termos de Uso regulam o acesso e utilização da plataforma Constela Brasil, seus serviços, conteúdos, funcionalidades, ferramentas digitais e demais recursos disponibilizados aos usuários, consteladores, parceiros e visitantes.',
    'Ao acessar ou utilizar a plataforma, o usuário declara ter lido, compreendido e aceitado integralmente estes Termos.',
  ],
};

export const termsSections: LegalSection[] = [
  {
    title: '1. Sobre a plataforma',
    paragraphs: [
      'A Constela Brasil é uma plataforma tecnológica destinada à conexão entre consteladores, facilitadores, alunos e participantes interessados em práticas de desenvolvimento pessoal, constelação familiar, treinamentos, mentorias, conteúdos educativos e experiências sistêmicas.',
      'A plataforma disponibiliza recursos digitais, incluindo:',
    ],
    items: [
      'Ambientes virtuais de constelação',
      'Ferramentas interativas',
      'Avatares e recursos visuais',
      'Área de membros',
      'Marketplace de profissionais',
      'Cursos e treinamentos',
      'Conteúdos digitais',
      'Integrações de comunicação online',
      'Sistemas de agendamento e atendimento',
    ],
    subsections: [
      {
        title: '',
        paragraphs: [
          'A Constela Brasil atua exclusivamente como intermediadora tecnológica, não sendo responsável pelos resultados individuais obtidos pelos usuários ou profissionais cadastrados.',
        ],
      },
    ],
  },
  {
    title: '2. Cadastro e acesso',
    paragraphs: [
      'Para utilização de determinadas funcionalidades, o usuário poderá realizar cadastro mediante fornecimento de informações verdadeiras, completas e atualizadas.',
      'O usuário declara ser responsável:',
    ],
    items: [
      'Pela veracidade das informações fornecidas',
      'Pela segurança de sua senha',
      'Pelo uso de sua conta',
      'Por todas as atividades realizadas em seu acesso',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['A plataforma poderá suspender ou cancelar contas em caso de:'],
        items: [
          'Informações falsas',
          'Uso indevido da plataforma',
          'Violação destes Termos',
          'Condutas abusivas ou ilegais',
          'Uso que comprometa a segurança da plataforma',
        ],
      },
    ],
  },
  {
    title: '3. Responsabilidades dos usuários',
    paragraphs: ['Ao utilizar a plataforma, o usuário compromete-se a:'],
    items: [
      'Respeitar a legislação vigente',
      'Agir com ética e boa-fé',
      'Respeitar outros participantes e profissionais',
      'Não divulgar conteúdos ofensivos, discriminatórios ou ilegais',
      'Não utilizar a plataforma para fraudes',
      'Não tentar acessar áreas restritas sem autorização',
      'Não copiar, reproduzir ou comercializar conteúdos sem autorização',
    ],
  },
  {
    title: '4. Responsabilidades dos consteladores e profissionais',
    paragraphs: [
      'Os profissionais cadastrados declaram possuir autonomia técnica e responsabilidade integral sobre:',
    ],
    items: [
      'Seus atendimentos',
      'Métodos aplicados',
      'Conteúdos divulgados',
      'Relação com seus clientes',
      'Informações fornecidas aos participantes',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['A Constela Brasil não garante e não se responsabiliza por:'],
        items: [
          'Resultados terapêuticos',
          'Resultados emocionais',
          'Resultados financeiros',
          'Resultados profissionais',
          'Cura física ou psicológica',
        ],
      },
      {
        title: '',
        paragraphs: ['Cada profissional é exclusivamente responsável pelos serviços prestados.'],
      },
    ],
  },
  {
    title: '5. Limitação de responsabilidade',
    paragraphs: ['A plataforma Constela Brasil não substitui:'],
    items: [
      'Atendimento médico',
      'Psicoterapia',
      'Psiquiatria',
      'Tratamento psicológico',
      'Tratamento clínico',
      'Atendimento jurídico',
      'Acompanhamento profissional especializado',
    ],
    subsections: [
      {
        title: '',
        paragraphs: [
          'As práticas disponibilizadas possuem finalidade de desenvolvimento pessoal, educacional e vivencial.',
          'A Constela Brasil não se responsabiliza por:',
        ],
        items: [
          'Decisões pessoais tomadas pelos usuários',
          'Interpretações individuais',
          'Reações emocionais decorrentes das práticas',
          'Condutas de profissionais cadastrados',
          'Perdas financeiras',
          'Danos indiretos',
          'Problemas decorrentes de falhas de internet ou dispositivos do usuário',
        ],
      },
    ],
  },
  {
    title: '6. Propriedade intelectual',
    paragraphs: ['Todos os elementos da plataforma, incluindo:'],
    items: [
      'Marca',
      'Nome comercial',
      'Logotipo',
      'Identidade visual',
      'Interface',
      'Sistema',
      'Textos',
      'Vídeos',
      'Materiais',
      'Avatares',
      'Recursos gráficos',
      'Metodologias',
      'Cursos',
      'Conteúdos',
    ],
    subsections: [
      {
        title: '',
        paragraphs: [
          'são protegidos por direitos autorais e propriedade intelectual.',
          'É proibida a reprodução, distribuição, cópia ou comercialização sem autorização expressa da Constela Brasil.',
        ],
      },
    ],
  },
  {
    title: '7. Pagamentos, assinaturas e cancelamentos',
    paragraphs: ['Os serviços pagos poderão incluir:'],
    items: [
      'Assinaturas',
      'Cursos',
      'Sessões',
      'Créditos',
      'Licenças',
      'Planos profissionais',
      'Recursos premium',
    ],
    subsections: [
      {
        title: '',
        paragraphs: [
          'Os valores poderão ser reajustados sem comunicação prévia.',
          'O cancelamento poderá ser solicitado conforme regras vigentes no momento da contratação.',
          'Reembolsos seguirão:',
        ],
        items: [
          'Código de Defesa do Consumidor',
          'Política de Reembolso da plataforma',
          'Regras específicas de cada serviço',
        ],
      },
      {
        title: '',
        paragraphs: ['A inadimplência poderá ocasionar:'],
        items: [
          'Suspensão de acesso',
          'Cancelamento de funcionalidades',
          'Restrição de serviços',
        ],
      },
    ],
  },
  {
    title: '8. Privacidade e proteção de dados',
    paragraphs: [
      'O tratamento de dados pessoais segue a Lei Geral de Proteção de Dados (LGPD).',
      'As informações completas estão descritas na Política de Privacidade.',
    ],
  },
  {
    title: '9. Alterações dos termos',
    paragraphs: [
      'A Constela Brasil poderá alterar estes Termos a qualquer momento.',
      'As atualizações entrarão em vigor após publicação no site.',
    ],
  },
  {
    title: '10. Foro',
    paragraphs: [
      'Fica eleito o foro da comarca de São Paulo/SP para resolução de eventuais conflitos relacionados a estes Termos.',
    ],
  },
  {
    title: 'Política de cancelamento e reembolso',
    subsections: [
      {
        title: '1. Cancelamento',
        paragraphs: [
          'O usuário poderá solicitar cancelamento conforme as condições aplicáveis ao serviço contratado.',
        ],
      },
      {
        title: '2. Reembolso',
        paragraphs: ['Os pedidos de reembolso observarão:'],
        items: [
          'Prazo legal de arrependimento de 7 (sete) dias',
          'Regras específicas de produtos digitais',
          'Consumo efetivo do conteúdo',
          'Utilização dos serviços',
        ],
      },
      {
        title: '3. Assinaturas',
        paragraphs: [
          'Assinaturas poderão ser renovadas automaticamente conforme o plano contratado.',
          'O cancelamento impede cobranças futuras, sem efeito retroativo.',
        ],
      },
    ],
  },
  {
    title: 'Contrato para consteladores e parceiros',
    paragraphs: ['Os profissionais cadastrados na plataforma concordam que:'],
    items: [
      'São responsáveis por seus próprios atendimentos',
      'Devem atuar dentro da ética profissional',
      'Não poderão utilizar a plataforma para práticas ilegais',
      'São responsáveis pelas informações divulgadas',
      'Devem respeitar os direitos dos participantes',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['A Constela Brasil poderá remover profissionais em caso de:'],
        items: [
          'Reclamações recorrentes',
          'Condutas inadequadas',
          'Violação destes Termos',
          'Danos à reputação da plataforma',
        ],
      },
    ],
  },
  {
    title: 'Aviso legal',
    paragraphs: [
      'A Constela Brasil é uma plataforma tecnológica.',
      'Não garantimos resultados específicos relacionados a:',
    ],
    items: [
      'Saúde',
      'Relacionamentos',
      'Questões emocionais',
      'Vida financeira',
      'Desenvolvimento profissional',
      'Questões familiares',
    ],
    subsections: [
      {
        title: '',
        paragraphs: [
          'Os conteúdos e práticas possuem finalidade educacional, vivencial e de desenvolvimento pessoal.',
        ],
      },
    ],
  },
  {
    title: 'Política de direitos autorais',
    paragraphs: [
      'Todos os conteúdos disponibilizados na plataforma são protegidos por legislação de propriedade intelectual.',
      'É proibido:',
    ],
    items: [
      'Copiar conteúdos',
      'Revender materiais',
      'Reproduzir cursos',
      'Distribuir arquivos sem autorização',
      'Utilizar a marca Constela Brasil indevidamente',
    ],
    subsections: [
      {
        title: '',
        paragraphs: ['Denúncias poderão ser encaminhadas pelos canais oficiais.'],
      },
    ],
  },
  {
    title: 'FAQ jurídico e de segurança',
    subsections: [
      {
        title: 'Meus dados estão protegidos?',
        paragraphs: [
          'Sim. A plataforma adota medidas de segurança e proteção de dados conforme a LGPD.',
        ],
      },
      {
        title: 'A plataforma grava sessões?',
        paragraphs: [
          'Eventuais gravações somente ocorrerão mediante aviso ou consentimento aplicável.',
        ],
      },
      {
        title: 'Posso cancelar minha assinatura?',
        paragraphs: [
          'Sim. O usuário poderá solicitar cancelamento conforme as regras do plano contratado.',
        ],
      },
      {
        title: 'A plataforma garante resultados terapêuticos?',
        paragraphs: [
          'Não. Os resultados dependem de diversos fatores individuais e da atuação de cada profissional.',
        ],
      },
      {
        title: 'Quem movimenta os avatares?',
        paragraphs: [
          'Os recursos visuais e avatares são manipulados pelos participantes ou profissionais conforme a dinâmica utilizada.',
        ],
      },
      {
        title: 'Preciso usar aplicativo de videochamada?',
        paragraphs: ['Não, a plataforma conta com videochamada integrada.'],
      },
      {
        title: 'Como posso tirar dúvidas?',
        paragraphs: ['O suporte poderá ser acessado pelos canais oficiais informados na plataforma.'],
      },
    ],
  },
  {
    title: 'Contato',
    paragraphs: [
      'Constela Brasil',
      'E-mail de suporte: contato@constelabrasil.com.br',
      'Site oficial: www.constelabrasil.com.br',
    ],
  },
  {
    title: 'Disposições finais',
    paragraphs: [
      'Ao utilizar a plataforma Constela Brasil, o usuário declara estar ciente e de acordo com todos os termos, políticas e condições aqui descritos.',
    ],
  },
];
